/**
 * Admin → Bot Archives.
 *
 * The bot backup zips contain the live `.env` + `sftp.yml`, so they are no
 * longer served publicly. This lists them from `/api/admin/bot-archives` and
 * builds download links that carry the admin access token in the query string
 * (a plain <a> can't send an Authorization header).
 */
import { useEffect, useState } from "react";
import { Archive, Download, ShieldAlert } from "lucide-react";
import { API, api, apiError, getAccess } from "@/lib/api";
import { Loader } from "@/components/Atoms";

function prettySize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

export default function BotArchives() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/admin/bot-archives")
      .then(({ data }) => setRows(data.archives || []))
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="space-y-4 fade-up" data-testid="bot-archives-panel">
      <div className="glass-card p-5 flex items-start gap-3">
        <ShieldAlert size={18} className="text-[var(--warning,#e5a53f)] mt-0.5 shrink-0" />
        <p className="text-sm text-[var(--muted)]">
          These archives carry the bot's <code>.env</code> and{" "}
          <code>data/sftp.yml</code>. They are admin-only and no longer
          downloadable from the public site. Treat any link you share as a
          credential leak.
        </p>
      </div>

      {error && (
        <div className="glass-card p-5 text-sm text-[var(--error)]" data-testid="bot-archives-error">
          {error}
        </div>
      )}

      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Archive size={16} />
          <h3 className="text-lg">Bot archives</h3>
          <span className="text-xs text-[var(--muted)]" data-testid="bot-archives-count">
            {rows.length} files
          </span>
        </div>
        <div className="divide-y divide-white/5">
          {rows.map((r) => (
            <div
              key={r.name}
              className="py-3 flex items-center gap-3 flex-wrap"
              data-testid={`bot-archive-row-${r.name}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{r.name}</div>
                <div className="text-[11px] text-[var(--muted)] font-mono truncate">
                  {r.directory} · {prettySize(r.size_bytes)}
                </div>
              </div>
              <a
                href={`${API}/admin/bot-archives/${encodeURIComponent(r.name)}?token=${encodeURIComponent(getAccess() || "")}`}
                className="btn btn-secondary flex items-center gap-1.5 text-xs"
                data-testid={`bot-archive-download-${r.name}`}
              >
                <Download size={13} /> Download
              </a>
            </div>
          ))}
          {rows.length === 0 && !error && (
            <p className="text-sm text-[var(--muted)] py-4" data-testid="bot-archives-empty">
              No archives on disk.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
